import React, { useState } from 'react'

import { Link, useHistory, useParams } from 'react-router-dom'

import { useFetch, useDownload, useAcceptPurchaseOrder, useQuestionPurchaseOrder, usePostInvoice, useRemoveInvoice } from '../../../../../hooks/vendor/purchaseOrder'
import { useDownload as useDownloadInvoice } from '../../../../../hooks/vendor/invoice'

import { PageHeader, Descriptions, Divider, Table, Popconfirm, Button, Space, Statistic, Tooltip } from 'antd'
import { QuestionOutlined, DownloadOutlined, CheckOutlined } from '@ant-design/icons'
import Moment from 'moment'
import { DATE_FORMAT } from '../../../../../core/utils/i18n-js'
import { AmountText } from '../../../../../core/components/widget/amountInline'
import StatisticAmount from '../../../../../core/components/widget/amountInline/statistic'
import { expandedPurchaseOrderTask } from '../../../../../components/job/finance/purchaseOrderTask'

import { LAYOUT_VENDOR_JOB_SHOW } from '../../../navigation'
import DisplayRectifaction from '../../../../../components/purchaseOrder/rectifications'

import Questions from './questions'

import InvoiceButton from '../../../../../components/invoice/form/buttonV2'
import InvoiceList from './invoices'

import Loader from '../../../../../components/loader'

const columnsAntd = (props) => {
  const { loading } = props

  if (loading) {
    return []
  }

  return [
    {
      title: 'Job',
      dataIndex: 'job_id',
      key: 'job',
      fixed: 'left',
      sorter: (a, b) => {
        return a.job_id > b.job_id
      },
      render: (job, record) => <Link to={LAYOUT_VENDOR_JOB_SHOW(job)}>{record.job_display_id}</Link>
    },
    {
      title: 'E-ID #',
      dataIndex: 'job_external_id',
      key: 'job',
      fixed: 'left',
      sorter: (a, b) => {
        return a.job_external_id > b.job_external_id
      },
      render: (job, record) => <>{job}</>
    },
    {
      title: 'Finance Date',
      dataIndex: 'finance_date',
      key: 'j.financeDate',
      sorter: (a, b) => {
        return a.finance_date > b.finance_date
      },
      render: (financeDate) => {
        if (financeDate) return <>{Moment(financeDate).format(DATE_FORMAT)}</>
      }
    },
    {
      title: 'Receivable Code',
      dataIndex: 'job_external_code',
      key: 'j.code'
    },

    {
      title: 'Service',
      dataIndex: 'service',
      key: 't.service'
    },
    {
      title: 'Amount',
      dataIndex: 'amount_value',
      key: 'amountValue',
      sorter: (a, b) => {
        return a.amount_value - b.amount_value
      },
      // eslint-disable-next-line react/display-name
      render: (jsa, record) => <AmountText value={record.amount_value} type={record.amount_type} />
    }
  ]
}

const hasRectification = (pot, task) => {
  return task.payables.some((p) => p.rectification)
}

const Content = ({ children, extra }) => {
  return (
    <div className='content'>
      {/* <img src="https://icon-library.com/images/translate-icon-png/translate-icon-png-9.jpg" alt="content" width="100px"/> */}
      <div className='main'>{children}</div>
      <div className='extra'>{extra}</div>
    </div>
  )
}

const extraContent = (data) => (
  <div
    style={{
      display: 'flex',
      width: 'max-content',
      justifyContent: 'flex-end'
    }}
  >
    <Statistic
      title='Status'
      value={data.purchaseOrder.status}
      style={{ marginRight: 32 }}
      valueStyle={{ color: '"#7e807f' }}
    />

    <StatisticAmount
      title='Amount'
      value={data.purchaseOrder.amount_value}
      prefix={data.purchaseOrder.amount_type}
    />
  </div>
)

const Show = (props) => {
  const history = useHistory()
  const [modalQuestion, setModalQuestion] = useState(false)
  const onPressBack = () => history.goBack()
  const { id } = useParams()
  const { data, isLoading, isError } = useFetch(id)

  const { mutate: downloadFile, isLoading: isDownloadLoading } = useDownload()
  const { mutate: downloadInvoice, isLoading: isDownloadInvoiceLoading } = useDownloadInvoice()
  const { mutate: acceptPurchaseOrder, isLoading: isAcceptLoading } = useAcceptPurchaseOrder(id)
  const { mutate: questionPurchaseOrder, isLoading: isQuestionLoading } = useQuestionPurchaseOrder(id)
  const { mutate: addInvoice, isLoading: isPostInvoiceLoading } = usePostInvoice(id)
  const { mutate: onRemoveInvoice, isLoading: isRemoveInvoiceLoading } = useRemoveInvoice(id)

  const actionLoading = isDownloadLoading || isAcceptLoading || isQuestionLoading || isPostInvoiceLoading || isRemoveInvoiceLoading || isDownloadInvoiceLoading

  if (isLoading) {
    return (<Loader loading />)
  }

  if (isError) {
    return (<>Error</>)
  }

  const displayRectifaction = (purchaseOrderTask) => {
    const task = (data.tasks || []).find((item) => item.id == purchaseOrderTask.task_id)
    return (
      <DisplayRectifaction
        purchaseOrderId={data.purchaseOrder.id}
        task={task}
        purchaseOrderTask={purchaseOrderTask}
        allowEdit={false}
        loading={actionLoading}
      />
    )
  }
  const expandedRowClassName = (record, index, indent) => 'table-finance-details'

  const expandedRowRender = (record) => {
    const task = (data.tasks || []).find((item) => item.id == record.task_id)

    if ((task.payables || []).length > 0) {
      return (
        <>
          {expandedPurchaseOrderTask(record, task)}
          {hasRectification(record, task) && displayRectifaction(record)}
        </>
      )
    }

    if (hasRectification(record, task)) {
      return displayRectifaction(record)
    }

    return null
  }

  const purchaseOrderActions = () => {
    if (data.purchaseOrder?.status === 'In Review') {
      return [
        <Tooltip key='question' title='Do you have questions about this PO?'>
          <Button
            size='small'
            disabled={actionLoading}
            onClick={() => setModalQuestion(true)}
          >
            <QuestionOutlined />
            <Divider type='vertical' />
            Questions
          </Button>
        </Tooltip>,
        <Popconfirm
          key='accepted'
          title='Are you sure?'
          disabled={actionLoading}
          onConfirm={acceptPurchaseOrder}
          okText='Yes'
          cancelText='No'
        >
          <Button type='primary' size='small' disabled={actionLoading}>
            <CheckOutlined />
            <Divider type='vertical' />
            Mark as accepted
          </Button>
        </Popconfirm>
      ]
    }

    if (data.purchaseOrder?.status === 'Accepted') {
      return [
        <InvoiceButton
          key='invoice'
          buttonLabel='Add invoice'
          onUpload={addInvoice}
          amountValue={data.purchaseOrder.amount_value}
          amountType={data.purchaseOrder.amount_type}
          fileRequired
        />
      ]
    }

    return []
  }

  return (
    <>
      <div className='site-page-header-ghost-wrapper'>
        <PageHeader
          ghost={false}
          className='site-page-header'
          onBack={onPressBack}
          title={`${data.purchaseOrder.display_id} / ${data.purchaseOrder.code}`}
          subTitle='Purchase Order'
          extra={purchaseOrderActions()}
        >
          <Content extra={extraContent(data)}>
            <Descriptions size='medium' column={2}>
              <Descriptions.Item label='Vendor'>{data.purchaseOrder?.vendor_name}</Descriptions.Item>
              <Descriptions.Item label='Date'>
                {' '}
                {Moment(data.purchaseOrder.date).format(DATE_FORMAT)}{' '}
              </Descriptions.Item>
              <Descriptions.Item label='Notes'> {data.purchaseOrder.notes} </Descriptions.Item>
            </Descriptions>
          </Content>
        </PageHeader>
      </div>

      <br />
      <InvoiceList
        invoices={data.purchaseOrder?.invoices}
        onDownloadInvoice={downloadInvoice}
        onRemoveInvoice={onRemoveInvoice}
        actionLoading={actionLoading}
      />
      <br />

      <PageHeader ghost={false}>
        <Space>
          <Button
            type='default'
            onClick={() =>
              downloadFile({ id: data.purchaseOrder.id, name: `${data.purchaseOrder.display_id}-${data.purchaseOrder.vendor_name}.xlsx` })
            }
            size='small'
          >
            <DownloadOutlined />
            <Divider type='vertical' />
            Download PO
          </Button>
        </Space>

        <br />
        <br />

        <Table
          bordered
          size='small'
          croll={{ x: 1300 }}
          rowKey={(row) => row.id}
          dataSource={data.purchaseOrder.purchase_order_tasks}
          columns={columnsAntd(props)}
          expandable={{
            expandedRowClassName,
            expandedRowRender,
            rowExpandable: (record) => true
          }}
        />
      </PageHeader>

      <Questions
        modalVisible={modalQuestion}
        onCancel={() => setModalQuestion(false)}
        onSubmit={questionPurchaseOrder}
        title='Purchase Order Questions'
        field='Questions'
      />
    </>
  )
}

export default Show
